import {
  ContactDuplicateListResponseSchema,
  ContactListResponseSchema,
  ContactSchema,
} from "@l30/shared";
import { keepPreviousData, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { buildQuery } from "./build-query.js";
import { apiFetch, apiFetchUnchecked } from "./client.js";

import type { Contact, ContactCreate, ContactUpdate, ContactListQuery } from "@l30/shared";

const CONTACTS_KEY = ["contacts"] as const;

export function useContacts(query?: Partial<ContactListQuery>, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...CONTACTS_KEY, query],
    queryFn: () => apiFetch(`/contacts${buildQuery(query)}`, {}, ContactListResponseSchema),
    enabled: options?.enabled ?? true,
    // Keep the current page on screen while the next one loads (assessment
    // U8) — paging Prev/Next never flashes an empty table.
    placeholderData: keepPreviousData,
  });
}

export function useContact(id: string) {
  return useQuery({
    queryKey: [...CONTACTS_KEY, id],
    queryFn: () => apiFetch(`/contacts/${id}`, {}, ContactSchema),
    enabled: !!id,
  });
}

export function useContactDuplicateCandidates(query?: { limit?: number }) {
  const params = new URLSearchParams();
  if (query?.limit) params.set("limit", String(query.limit));
  const qs = params.toString();
  return useQuery({
    queryKey: [...CONTACTS_KEY, "duplicates", query],
    queryFn: () =>
      apiFetch(`/contacts/duplicates${qs ? `?${qs}` : ""}`, {}, ContactDuplicateListResponseSchema),
  });
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ContactCreate) =>
      apiFetchUnchecked<Contact>("/contacts", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CONTACTS_KEY }),
  });
}

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: ContactUpdate & { id: string }) =>
      apiFetchUnchecked<Contact>(`/contacts/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CONTACTS_KEY }),
  });
}

/** Hard-delete a contact. Admin only — server returns 403 otherwise. */
export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetchUnchecked<void>(`/contacts/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CONTACTS_KEY }),
  });
}

/** Soft-delete: archive. Auto-removes the contact from all groups. */
export function useArchiveContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<Contact>(`/contacts/${id}/archive`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CONTACTS_KEY });
      qc.invalidateQueries({ queryKey: ["contact-groups"] });
    },
  });
}

/** Unarchive — admin only. */
export function useUnarchiveContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<Contact>(`/contacts/${id}/unarchive`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CONTACTS_KEY }),
  });
}

export function useMergeContacts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { sourceId: string; targetId: string }) =>
      apiFetch<Contact>(
        "/contacts/merge",
        {
          method: "POST",
          body: JSON.stringify(data),
        },
        ContactSchema,
      ),
    onSuccess: (contact) => {
      qc.invalidateQueries({ queryKey: CONTACTS_KEY });
      qc.invalidateQueries({ queryKey: [...CONTACTS_KEY, "duplicates"] });
      qc.invalidateQueries({ queryKey: [...CONTACTS_KEY, contact.id] });
      qc.invalidateQueries({ queryKey: ["contact-groups"] });
    },
  });
}
